import { useState } from 'react';
import { TILE_GROUPS, tilesToBrief, type Tile, type TileChoice, type Brief } from '@studio/shared';
import { useVoice } from '../useVoice.js';

type Step = 'world' | 'character' | 'action' | 'goal' | 'extra';

/** How you win is optional, so the last tile step can be skipped. */
const STEPS: Step[] = ['world', 'character', 'action', 'goal', 'extra'];

type Props = {
  busy: boolean;
  onSubmit: (brief: Brief) => void;
};

/**
 * The youngest children's way in.
 *
 * One question on screen at a time. A grid of every choice at once is how an
 * adult expects to browse and how a six-year-old freezes — she needs one thing
 * to answer, big enough to hit with a whole finger, and visible progress that
 * she is getting somewhere.
 */
export function SproutForm({ busy, onSubmit }: Props) {
  const [step, setStep] = useState<Step>('world');
  const [choice, setChoice] = useState<TileChoice>({});
  const voice = useVoice((text) => setChoice((c) => ({ ...c, extra: text })));

  const index = STEPS.indexOf(step);
  const brief = tilesToBrief(choice);

  const pick = (key: Step, tile: Tile) => {
    setChoice({ ...choice, [key]: tile.id });
    const next = STEPS[index + 1];
    if (next) setStep(next);
  };

  const group = step !== 'extra' ? TILE_GROUPS[step] : null;

  return (
    <section className="sprout">
      <div className="sprout__dots" aria-hidden>
        {STEPS.map((s, i) => (
          <span key={s} className={i <= index ? 'dot dot--on' : 'dot'} />
        ))}
      </div>

      {group && (
        <>
          <h2>{group.question}</h2>
          <div className="sprout__tiles">
            {group.tiles.map((tile) => (
              <button
                key={tile.id}
                type="button"
                className={choice[step] === tile.id ? 'tile tile--on' : 'tile'}
                onClick={() => pick(step, tile)}
              >
                <span className="tile__emoji">{tile.emoji}</span>
                <span className="tile__label">{tile.label}</span>
              </button>
            ))}
          </div>
          {step === 'goal' && (
            <button type="button" className="sprout__skip" onClick={() => setStep('extra')}>
              I don't mind
            </button>
          )}
        </>
      )}

      {step === 'extra' && (
        <>
          <h2>Anything else about your game?</h2>
          <p className="sprout__hint">You can tell me, or just make it now.</p>

          {voice.supported && (
            <button
              type="button"
              className={voice.listening ? 'mic mic--live' : 'mic'}
              onClick={() => (voice.listening ? voice.stop() : voice.start())}
            >
              <span className="mic__icon">{voice.listening ? '👂' : '🎤'}</span>
              {voice.listening ? "I'm listening…" : 'Tell me'}
            </button>
          )}

          {choice.extra && (
            <p className="sprout__said">
              You said: “{choice.extra}”
              <button type="button" onClick={() => setChoice({ ...choice, extra: '' })}>
                Clear
              </button>
            </p>
          )}
        </>
      )}

      <div className="sprout__nav">
        {index > 0 && (
          <button type="button" className="sprout__back" onClick={() => setStep(STEPS[index - 1]!)}>
            Back
          </button>
        )}
        {brief && (
          <button
            type="button"
            className="go"
            disabled={busy}
            onClick={() => onSubmit(brief)}
          >
            {busy ? 'Making it…' : 'Make it! ✨'}
          </button>
        )}
      </div>
    </section>
  );
}
